import { useMemo } from 'react'
import {
  calculateDiscounts,
  getPriceFromCalculation,
  getPriceFromCalculations,
  getRegisteredPartnersFromState
} from '../../../pure-js/libs/calculateLogic/CalculateDiscountHelpers'
import { Activity, Client, VatData } from '../../../pure-js/types/GrooverTypes'
import { State } from '../hooks/useAppState'
import { Calculation } from '../../../pure-js/libs/calculateLogic/CalculateDiscountTypes'
import { getVatFromBruttoPrice } from '../../../pure-js/libs/EnrollmentHelper'
import { PriceOverride } from '../../../pure-js/types/DiscountTypes'

interface CalculationParams {
  activity?: Activity
  client?: Client
  state: State
}

// Pure calculation function
export const calculateDiscountData = ({ activity, client, state }: CalculationParams) => {
  const hasMainUser = (!!state.name && !!state.email && !!state.phoneNumber) || !!state.obfuscatedEnrollment?.mainUser

  const isScopedEnrollmentViewer =
    typeof window !== 'undefined' &&
    !!state.obfuscatedEnrollment?.id &&
    !!new URLSearchParams(window.location.search).get('u')?.trim()

  const hasPartner = isScopedEnrollmentViewer
    ? !!state.obfuscatedEnrollment?.partnerUser
    : (!!state?.partnerName && !!state?.partnerEmail && !!state?.partnerPhoneNumber && !!state?.partnerRole) ||
      !!state.obfuscatedEnrollment?.partnerUser

  const discountCode = state.discountCode || state.obfuscatedEnrollment?.discountUserInput?.discountCode || undefined

  // For SingleEvent activities, use the quantity from state
  const isSingleEvent = activity?.eventType === 'SingleEvent'
  const singleEventQuantity =
    (activity?.id && state.obfuscatedEnrollment?.activityQuantities?.[activity.id]) || state?.singleEventQuantity || 1

  const participantsAmount = hasPartner || !!state.obfuscatedEnrollment?.partnerUser?.phone ? 2 : 1

  // Create price override from early bird bookings
  const priceOverride: PriceOverride[] = Object.keys(state.earlyBirdBooking || {}).map((activityId) => {
    const earlyBirdBooking = state.earlyBirdBooking[activityId]
    const activity = state.selectedActivities.find((a) => a.id === activityId)

    // Find the pricing config to get the label
    let label = 'Early Bird' // fallback
    if (activity?.discountConfig && earlyBirdBooking.pricingConfigId) {
      const pricingConfig = activity.discountConfig.pricingConfigs.find(
        (config) => config.id === earlyBirdBooking.pricingConfigId
      )
      if (pricingConfig?.label) {
        label = pricingConfig.label
      }
    }

    return {
      activityId,
      price: earlyBirdBooking.price,
      reference: earlyBirdBooking.bookingId,
      type: 'earlyBird' as const,
      label
    }
  })

  let mainUserPrices = [] as Calculation[]
  let partnerUserPrices = [] as Calculation[]
  let finalPrice = 0
  let vatData: VatData = {}

  if (activity && client) {
    // Create activityQuantities object for SingleEvent activities
    const activityQuantities: Record<string, number> = {}
    if (isSingleEvent && activity) {
      activityQuantities[activity.id] = singleEventQuantity
    }

    const activitiesToCalculate = !!state.obfuscatedEnrollment?.activities
      ? state.obfuscatedEnrollment?.activities
      : state?.selectedActivities

    // Use the existing complex discount calculation system for all activities
    let userActivePeriods =
      (state.obfuscatedEnrollment?.discountUserInput as any)?.mainUser?.userActivePeriods || state.mainUserActivePeriods

    mainUserPrices = calculateDiscounts(activitiesToCalculate, client, {
      // @ts-ignore
      isStudent: state.obfuscatedEnrollment?.discountUserInput?.mainUser?.isStudent || state.isMainUserStudent,
      // @ts-ignore
      isSibling: state.obfuscatedEnrollment?.discountUserInput?.mainUser?.isSibling || state.isMainUserSibling,
      // @ts-ignore
      isSenior: state.obfuscatedEnrollment?.discountUserInput?.mainUser?.isSenior || state.isMainUserSenior,
      // @ts-ignore
      userActivePeriods,
      userEnrolledActivityIds: state.mainUserEnrolledActivityIds,
      userEnrolledPeriodIds: state.mainUserEnrolledPeriodIds,
      userEnrolledActivityPeriods: state.mainUserEnrolledActivityPeriods,
      usedContinuationDiscountIds: state.mainUsedContinuationDiscountIds,
      usedContinuationDiscountActivities: state.mainUsedContinuationDiscountActivities,
      participantsAmount,
      advancedRegistration: {
        type: state.discountType,
        activityId: activity.id,
        region: state.discountRegion
      },
      registeredPartners: getRegisteredPartnersFromState(state.registeredPartners),
      priceOverride,
      activityQuantities,
      discountCode: discountCode
    })

    if (hasPartner) {
      partnerUserPrices = calculateDiscounts(
        !!state.obfuscatedEnrollment?.activities ? state.obfuscatedEnrollment?.activities : state?.selectedActivities,
        client,
        {
          // @ts-ignore
          isStudent: state.obfuscatedEnrollment?.discountUserInput?.partnerUser?.isStudent || state.isPartnerStudent,
          // @ts-ignore
          isSibling: state.obfuscatedEnrollment?.discountUserInput?.partnerUser?.isSibling || state.isPartnerSibling,
          // @ts-ignore
          isSenior: state.obfuscatedEnrollment?.discountUserInput?.partnerUser?.isSenior || state.isPartnerSenior,
          // @ts-ignore
          userActivePeriods:
            // @ts-ignore
            state.obfuscatedEnrollment?.discountUserInput?.partnerUser?.userActivePeriods ||
            state.partnerUserActivePeriods,
          userEnrolledActivityIds: state.partnerUserEnrolledActivityIds,
          userEnrolledPeriodIds: state.partnerUserEnrolledPeriodIds,
          userEnrolledActivityPeriods: state.partnerUserEnrolledActivityPeriods,
          usedContinuationDiscountIds: state.partnerUsedContinuationDiscountIds,
          usedContinuationDiscountActivities: state.partnerUsedContinuationDiscountActivities,
          participantsAmount,
          // TODO advancedRegistration
          registeredPartners: getRegisteredPartnersFromState(state.registeredPartners),
          priceOverride,
          activityQuantities,
          discountCode: discountCode
        }
      )
    }

    const calculateUserTotal = (prices: Calculation[]) => {
      const subtotal = getPriceFromCalculations(prices) / 100
      finalPrice += subtotal

      prices.forEach((calculation) => {
        const price = getPriceFromCalculation(calculation) / 100
        const vat = calculation.activity?.vat

        if (vat !== undefined) {
          if (!vatData[vat]) vatData[vat] = 0
          vatData[vat] += getVatFromBruttoPrice(price, vat)
          vatData[vat] = Math.round(vatData[vat] * 100) / 100
        }
      })
    }

    calculateUserTotal(mainUserPrices)
    if (partnerUserPrices) calculateUserTotal(partnerUserPrices)
  }

  const finalPriceMainUser = getPriceFromCalculations(mainUserPrices) / 100
  const finalPricePartnerUser = getPriceFromCalculations(partnerUserPrices) / 100

  return {
    hasMainUser,
    hasPartner,
    mainUserPrices,
    partnerUserPrices,
    finalPrice,
    finalPriceMainUser,
    finalPricePartnerUser,
    vatData
  }
}

const useDiscountCalculations = (params: CalculationParams) => {
  return useMemo(
    () => calculateDiscountData(params),
    [
      params.activity,
      params.client,
      params.state,
      params.state.selectedActivities,
      params.state.mainUserActivePeriods,
      params.state.partnerUserActivePeriods,
      params.state.mainUserEnrolledActivityIds,
      params.state.mainUserEnrolledPeriodIds,
      params.state.mainUserEnrolledActivityPeriods,
      params.state.mainUsedContinuationDiscountIds,
      params.state.mainUsedContinuationDiscountActivities,
      params.state.partnerUserEnrolledActivityIds,
      params.state.partnerUserEnrolledPeriodIds,
      params.state.partnerUserEnrolledActivityPeriods,
      params.state.partnerUsedContinuationDiscountIds,
      params.state.partnerUsedContinuationDiscountActivities,
      params.state.earlyBirdBooking,
      params.state.singleEventQuantity,
      params.state.discountCode,
      params.state.discountType,
      params.state.discountRegion,
      params.state.registeredPartners,
      params.state.obfuscatedEnrollment
    ]
  )
}

export default useDiscountCalculations
